import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { FUEL_OPTIONS, BODY_TYPE_OPTIONS, STORAGE_OPTIONS, AIRBAG_OPTIONS, ANTI_THEFT_OPTIONS } from "@/lib/policyConstants";
import { DatePicker } from "@/components/DatePicker";

/**
 * Dettagli tecnici veicolo per polizza auto.
 * Visualizzati in 3 sezioni a colori tenui per migliore leggibilità:
 *  - Identità & immatricolazione (azzurro)
 *  - Caratteristiche motore (verde)
 *  - Sicurezza & equipaggiamento (ambra)
 */
export function PolicyVehicleDetails({ form, onChange }) {
  return (
    <div className="col-span-2 mt-2 space-y-3" data-testid="policy-vehicle-details-extended">
      <Label className="text-sm font-semibold flex items-center gap-2 text-slate-700">
        Dettagli tecnici veicolo
        <span className="text-[10px] font-normal text-slate-400">(opzionali, utili per preventivo e gestione)</span>
      </Label>

      {/* Sezione 1: Identità & immatricolazione (azzurro tenue) */}
      <fieldset className="rounded-lg border border-sky-200 bg-sky-50/50 p-3">
        <legend className="px-2 text-[11px] font-semibold uppercase tracking-wider text-sky-700 bg-white rounded">
          🪪 Identità & immatricolazione
        </legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">1ª immatricolazione</Label>
            <DatePicker data-testid="veh-first-reg" max={new Date().toISOString().slice(0,10)}
              min="1900-01-01"
              value={form.vehicle_first_registration || ""}
              onChange={(e) => {
                const v = e.target.value;
                if (v) {
                  const d = new Date(v);
                  const today = new Date(); today.setHours(0,0,0,0);
                  const minD = new Date("1900-01-01");
                  if (d > today) {
                    if (typeof window !== "undefined") window.alert("La data di immatricolazione non può essere nel futuro.");
                    return;
                  }
                  if (d < minD) {
                    if (typeof window !== "undefined") window.alert("La data di immatricolazione non può essere precedente al 1900.");
                    return;
                  }
                }
                onChange({ vehicle_first_registration: v });
              }} />
            {form.vehicle_first_registration && (() => {
              const d = new Date(form.vehicle_first_registration);
              const yearsOld = Math.floor((Date.now() - d.getTime()) / (365.25 * 86400000));
              if (isNaN(yearsOld)) return null;
              return (
                <p className="text-[10px] text-slate-500 mt-0.5 tabular-nums">
                  Veicolo di <strong>{yearsOld}</strong> ann{yearsOld === 1 ? "o" : "i"}
                  {yearsOld >= 30 && <span className="ml-1 text-amber-700 font-semibold">· d&apos;epoca?</span>}
                </p>
              );
            })()}
          </div>
          <div>
            <Label className="text-xs">Data acquisto veicolo</Label>
            <DatePicker data-testid="veh-purchase-date" max={new Date().toISOString().slice(0,10)}
              min={form.vehicle_first_registration || "1900-01-01"}
              value={form.vehicle_purchase_date || ""}
              onChange={(e) => {
                const v = e.target.value;
                if (v) {
                  // 🆕 Confronto come stringa YYYY-MM-DD (evita bug di fuso orario)
                  const todayIso = new Date();
                  todayIso.setMinutes(todayIso.getMinutes() - todayIso.getTimezoneOffset());
                  const todayStr = todayIso.toISOString().slice(0, 10);
                  if (v > todayStr) {
                    if (typeof window !== "undefined") window.alert("La data di acquisto non può essere nel futuro.");
                    return;
                  }
                  if (form.vehicle_first_registration && v < form.vehicle_first_registration) {
                    if (typeof window !== "undefined") window.alert("La data di acquisto non può precedere la 1ª immatricolazione.");
                    return;
                  }
                }
                onChange({ vehicle_purchase_date: v });
              }} />
            {form.vehicle_purchase_date && (() => {
              const d = new Date(form.vehicle_purchase_date);
              const monthsOwned = Math.floor((Date.now() - d.getTime()) / (30.44 * 86400000));
              if (isNaN(monthsOwned) || monthsOwned < 0) return null;
              const years = Math.floor(monthsOwned / 12);
              const months = monthsOwned % 12;
              return (
                <p className="text-[10px] text-slate-500 mt-0.5 tabular-nums">
                  Posseduto da <strong>{years > 0 ? `${years} ann${years === 1 ? "o" : "i"}` : ""}{years > 0 && months > 0 ? " e " : ""}{months > 0 || years === 0 ? `${months} mes${months === 1 ? "e" : "i"}` : ""}</strong>
                </p>
              );
            })()}
          </div>
          <div>
            <Label className="text-xs">Carrozzeria</Label>
            <Select value={form.vehicle_body_type || ""} onValueChange={(v) => onChange({ vehicle_body_type: v })}>
              <SelectTrigger data-testid="veh-body-type"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {BODY_TYPE_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Alimentazione</Label>
            <Select value={form.vehicle_fuel || ""} onValueChange={(v) => onChange({ vehicle_fuel: v })}>
              <SelectTrigger data-testid="veh-fuel"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {FUEL_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </fieldset>

      {/* Sezione 2: Caratteristiche motore (verde tenue) */}
      <fieldset className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3">
        <legend className="px-2 text-[11px] font-semibold uppercase tracking-wider text-emerald-700 bg-white rounded">
          ⚙️ Caratteristiche motore
        </legend>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Potenza fiscale (CV)</Label>
            <Input
              data-testid="veh-fiscal-power"
              type="number"
              min="0"
              value={form.vehicle_fiscal_power ?? ""}
              onChange={(e) => onChange({ vehicle_fiscal_power: e.target.value === "" ? "" : parseInt(e.target.value, 10) })}
            />
          </div>
          <div>
            <Label className="text-xs">KW</Label>
            <Input
              data-testid="veh-kw"
              type="number"
              min="0"
              value={form.vehicle_kw ?? ""}
              onChange={(e) => onChange({ vehicle_kw: e.target.value === "" ? "" : parseInt(e.target.value, 10) })}
            />
          </div>
          <div>
            <Label className="text-xs">Cilindrata (cc)</Label>
            <Input
              data-testid="veh-displacement"
              type="number"
              min="0"
              value={form.vehicle_displacement ?? ""}
              onChange={(e) => onChange({ vehicle_displacement: e.target.value === "" ? "" : parseInt(e.target.value, 10) })}
            />
          </div>
        </div>
      </fieldset>

      {/* Sezione 3: Sicurezza & equipaggiamento (ambra tenue) */}
      <fieldset className="rounded-lg border border-amber-200 bg-amber-50/50 p-3">
        <legend className="px-2 text-[11px] font-semibold uppercase tracking-wider text-amber-700 bg-white rounded">
          🛡️ Sicurezza & equipaggiamento
        </legend>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Airbag di serie</Label>
            <Select value={form.vehicle_airbags || ""} onValueChange={(v) => onChange({ vehicle_airbags: v })}>
              <SelectTrigger data-testid="veh-airbags"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {AIRBAG_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Antifurto</Label>
            <Select value={form.vehicle_anti_theft || ""} onValueChange={(v) => onChange({ vehicle_anti_theft: v })}>
              <SelectTrigger data-testid="veh-anti-theft"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {ANTI_THEFT_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Custodia</Label>
            <Select value={form.vehicle_storage || ""} onValueChange={(v) => onChange({ vehicle_storage: v })}>
              <SelectTrigger data-testid="veh-storage"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {STORAGE_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Dispositivi di sicurezza</Label>
            <Input
              data-testid="veh-safety"
              placeholder="es. ABS, ESP, controllo trazione..."
              value={form.vehicle_safety_devices || ""}
              onChange={(e) => onChange({ vehicle_safety_devices: e.target.value })}
            />
          </div>
          <div className="flex items-center gap-2 pt-5">
            <input
              id="veh-tow"
              data-testid="veh-tow"
              type="checkbox"
              className="h-4 w-4"
              checked={!!form.vehicle_tow_hook}
              onChange={(e) => onChange({ vehicle_tow_hook: e.target.checked })}
            />
            <label htmlFor="veh-tow" className="text-sm cursor-pointer">Gancio traino</label>
          </div>
        </div>
      </fieldset>
    </div>
  );
}
